<template>
  <div v-if="project">
    <b-row class="match-height">
      <b-col
        cols="12"
        md="6"
      >
        <b-card>
          <b-card-body class="p-0">
            <table class="mt-2 mt-xl-0 w-100 table table-striped table-borderless table-sm">
              <tbody>
                <tr
                  v-for="(info, index) in informations"
                  :key="index"
                >
                  <th class="w-25">
                    <span class="font-weight-bold line-height-1">{{ info.title }}</span>
                  </th>
                  <td><span class="line-height-1 small">{{ info.value }}</span></td>
                </tr>
              </tbody>
            </table>
            <hr>
            <attachments-viewer
              v-model="project.attachments"
              :refaid="refaid"
            />
          </b-card-body></b-card>
      </b-col>

      <b-col
        cols="12"
        md="6"
      >
        <b-card>
          <b-card-body class="p-0">
            <b-form-group
              :label="$t('projects.filter-tags-label')"
              label-for="tags"
            >
              <div class="d-flex align-items-center justify-content-end">
                <tags-selector
                  v-model="project.tags"
                  :taggable="true"
                  :tags-options="tagsOptions"
                  :tags-colors="tagsColors"
                  :disabled="!isUserAllowWrite"
                />
              </div>
            </b-form-group>
            <b-form-group
              :label="$t('common.notes')"
              label-for="notes"
            >
              <div class="form-label-group ">
                <quill-editor
                  id="notes"
                  v-model="project.infos.notes"
                  class="quill"
                  :disabled="!isUserAllowWrite"
                  :options="quillEditorOptions"
                />
              </div>
            </b-form-group>

            <b-form-group
              v-if="isMultiArea && !project.isChildren"
              :label="$t('projects.link-to-label')"
              label-for="link-to"
            >
              <div
                v-if="!project.links"
                class="d-flex align-items-center justify-content-end"
              >
                <span class="w-100"><small>{{ $t('projects.no-links') }}</small></span>
                <b-button-group
                  v-if="isUserAllowWrite && isUserAllowNetworking "
                >
                  <b-button
                    class="btn-icon "
                    variant="outline-primary"
                    @click="link()"
                  >
                    <span class="text-nowrap ">{{ $t('projects.link-to-areas') }}</span>
                  </b-button>
                </b-button-group>
              </div>

              <div
                v-if="project.links"
                class="d-flex align-items-center justify-content-end"
              >
                <span class="w-100"><small><strong>{{ $t('projects.linked') }}</strong> - {{ formatDateHumanFriendly(project.links.lastUpdate) }}</small></span>
                <b-button-group
                  v-if="isUserAllowWrite && isUserAllowNetworking "
                >
                  <b-button
                    class="btn-icon "
                    variant="outline-primary"
                    @click="unlink()"
                  >
                    <span class="text-nowrap ">{{ $t('projects.remove-links') }}</span>
                  </b-button>
                </b-button-group>
              </div>

            </b-form-group>
          </b-card-body></b-card>
      </b-col>
    </b-row>
  </div>

</template>

<script>
/* eslint-disable import/no-extraneous-dependencies */
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
/* eslint-disable no-nested-ternary */
import {
  BRow, BCol, BFormGroup, BCardBody, BCard, BButtonGroup, BButton,
} from 'bootstrap-vue'
import { quillEditor } from 'vue-quill-editor'
import i18n from '@/libs/i18n'
import authService from '@/vue.auth.service'
import {
  avatarText, resolveAvatarVariant, resolveStatusVariant, formatDate, formatAge, formatAgeYear, formatDateHumanFriendly, formatCurrency, quillEditorOptions,
} from '../layouts/layouts.utils'
import TagsSelector from '../layouts/tables/TagsSelector.vue'
import AttachmentsViewer from '../attachments/AttachmentsViewer.vue'

export default {
  components: {
    BRow, BCol, BFormGroup, BCard, BCardBody, BButtonGroup, BButton, TagsSelector, AttachmentsViewer, quillEditor,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    // eslint-disable-next-line vue/require-prop-types
    refaid: {
      required: true,
    },
    tagsOptions: {
      type: Array,
      default: null,
    },
    tagsColors: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      isUserAllowWrite: authService.isUserInRole('write'),
      isMultiArea: false,
      isUserAllowNetworking: false,
    }
  },
  computed: {
    project: {
      get() { return this.value },
      set(value) { this.$emit('input', value) },
    },
    informations() {
      const infos = []
      if (this.project && this.project.isChildren === true) {
        const data = this.project.infos
        infos.push({ icon: '', title: i18n.t('projects.sad-code'), value: data.projectCode })
        infos.push({ icon: 'Check', title: i18n.t('projects.gender'), value: (data.gender === 'F' ? i18n.t('common.gender-F') : (data.gender === 'M' ? i18n.t('common.gender-M') : null)) })
        infos.push({ icon: 'Calendar', title: i18n.t('projects.birth-date'), value: (data.birthDate ? formatDate(data.birthDate, 'DD MMM yyyy') : null) })
        infos.push({ icon: 'Calendar', title: i18n.t('projects.birth-year'), value: data.birthYear })
        infos.push({ icon: 'Clock', title: i18n.t('projects.age'), value: (data.birthDate ? formatAge(new Date(data.birthDate)) : (data.birthYear ? formatAgeYear(data.birthYear) : null)) })
        infos.push({ icon: 'Pocket', title: i18n.t('projects.school-class'), value: data.schoolClass })
        infos.push({ icon: '', title: i18n.t('projects.triboo'), value: data.triboo })
        infos.push({ icon: 'Globe', title: i18n.t('projects.mission'), value: data.missionName })
        infos.push({ icon: 'Globe', title: i18n.t('projects.nation'), value: data.nation })
        infos.push({ icon: 'MapPin', title: i18n.t('projects.address-line'), value: data.addressLine })
        infos.push({ icon: 'Edit3', title: i18n.t('common.last-update'), value: `${formatDateHumanFriendly(this.project.lastUpdate)} - ${this.project.editor}` })
      } else if (this.project && this.project.isChildren === false) {
        const data = this.project.infos
        infos.push({ icon: '', title: i18n.t('projects.external-name'), value: data.externalName })
        infos.push({ icon: '', title: i18n.t('projects.mission'), value: data.missionName })
        infos.push({ icon: 'MapPin', title: i18n.t('projects.filter-project-type'), value: data.projectType })
        infos.push({ icon: 'Globe', title: i18n.t('projects.nation'), value: `${data.nation ? data.nation : '-'} / ${data.zone ? data.zone : '-'}` })
        infos.push({ icon: 'Calendar', title: i18n.t('projects.project-date'), value: (data.projectDate ? formatDate(data.projectDate, 'DD MMM yyyy') : null) })
        infos.push({ icon: 'Pocket', title: i18n.t('projects.project-code'), value: data.projectCode })
        infos.push({ icon: 'Cloud', title: i18n.t('projects.web'), value: data.web })
        infos.push({ icon: 'Info', title: i18n.t('projects.project-inspectory'), value: data.inspectory })
        infos.push({ icon: 'Info', title: i18n.t('projects.found-from'), value: data.foundFrom })
        infos.push({ icon: 'Info', title: i18n.t('projects.referrals'), value: data.referrals })
        infos.push({ icon: '', title: i18n.t('projects.total-amount'), value: formatCurrency(data.totalAmount, data.totalCurrency) })
        infos.push({ icon: 'Edit3', title: i18n.t('common.last-update'), value: `${formatDateHumanFriendly(this.project.lastUpdate)} - ${this.project.editor}` })
      }
      return infos.filter(i => i.value)
    },
  },
  async mounted() {
    this.isMultiArea = await authService.isMultiArea()
    const areas = await authService.getAllowedAreas()
    this.isUserAllowNetworking = areas.length > 1
  },

  methods: {
    link() {
      if (!this.project.links) {
        this.project.links = { areas: [], lastUpdate: new Date() }
        this.$emit('on-submit')
      }
    },
    unlink() {
      if (this.project.links) {
        this.project.links.pleaseDelete = true
        this.$emit('on-submit')
      }
    },
  },
  setup() {
    return {
      avatarText,
      resolveAvatarVariant,
      resolveStatusVariant,
      formatDateHumanFriendly,
      quillEditorOptions,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
